import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';

// Row virtualization for antd v4 Table WITHOUT giving up native <tr>/<td>
// rendering. We override the tbody (`components.body.wrapper`). antd/rc-table
// still builds every BodyRow element (cheap), but we only mount the rows in the
// visible window (plus a small overscan) and pad the rest with two spacer rows.
// Native rows mean row colors (CSS on <tr>), checkbox selection and onRow keep
// working, and the header/body stay aligned via antd's own colgroup.
//
// IMPORTANT: with scroll.y + horizontal scroll, rc-table renders a hidden
// "measure row" as the first child of the tbody and uses a ResizeObserver on it
// to sync the real column widths into both the header and body colgroups. That
// row (and the empty placeholder) has NO rowKey. We must keep it mounted at all
// times — if virtualization slices it out when scrolled, the measured widths
// collapse and the body columns stop lining up with the header. So we virtualize
// ONLY the data rows (which carry a rowKey) and always render the rest.
//
// Requirements:
//  - Fixed body height via scroll.y (so antd creates the `.ant-table-body`
//    scroll container we listen to).
//  - Every column has a width (fixed table layout, complete colgroup).
//  - Roughly uniform row heights; we measure the first row and adapt.

const OVERSCAN = 6;

const isDataRow = (el) => !!(el && el.props && el.props.rowKey !== undefined);

export function createVirtualBody(estimatedRowHeight) {
    return function VirtualTbody({className, children}) {
        const ref = useRef(null);
        const rowHeightRef = useRef(estimatedRowHeight);

        const all = React.Children.toArray(children);
        const dataRows = [];
        const otherRows = []; // measure row, empty placeholder, etc. — never sliced
        all.forEach(el => (isDataRow(el) ? dataRows : otherRows).push(el));
        const total = dataRows.length;

        const [range, setRange] = useState({start: 0, end: Math.min(total, 40)});

        useLayoutEffect(() => {
            const firstRow = ref.current?.querySelector('tr[data-row-key]');
            if (firstRow) {
                const h = firstRow.getBoundingClientRect().height;
                if (h && Math.abs(h - rowHeightRef.current) > 1) {
                    rowHeightRef.current = h;
                }
            }
        });

        useEffect(() => {
            const scroller = ref.current?.closest('.ant-table-body');
            if (!scroller) return; // no scroll.y => cannot virtualize, render all

            const compute = () => {
                const rowHeight = rowHeightRef.current || estimatedRowHeight;
                const st = scroller.scrollTop;
                const h = scroller.clientHeight || 0;
                const start = Math.max(0, Math.floor(st / rowHeight) - OVERSCAN);
                const end = Math.min(total, Math.ceil((st + h) / rowHeight) + OVERSCAN);
                setRange(prev => (prev.start === start && prev.end === end ? prev : {start, end}));
            };

            compute();
            scroller.addEventListener('scroll', compute, {passive: true});
            window.addEventListener('resize', compute);
            return () => {
                scroller.removeEventListener('scroll', compute);
                window.removeEventListener('resize', compute);
            };
        }, [total, estimatedRowHeight]);

        // Safe fallback: if there is no scroll container, render everything.
        const scroller = ref.current?.closest('.ant-table-body');
        if (!scroller && ref.current) {
            return <tbody ref={ref} className={className}>{otherRows}{dataRows}</tbody>;
        }

        const rowHeight = rowHeightRef.current || estimatedRowHeight;
        const start = Math.min(range.start, total);
        const end = Math.min(range.end, total);
        const topPad = start * rowHeight;
        const bottomPad = Math.max(0, (total - end) * rowHeight);

        return (
            <tbody ref={ref} className={className}>
                {otherRows}
                {topPad > 0 && (
                    <tr key="virtual-top-spacer" aria-hidden style={{height: topPad}}>
                        <td colSpan={9999} style={{padding: 0, border: 0}}/>
                    </tr>
                )}
                {dataRows.slice(start, end)}
                {bottomPad > 0 && (
                    <tr key="virtual-bottom-spacer" aria-hidden style={{height: bottomPad}}>
                        <td colSpan={9999} style={{padding: 0, border: 0}}/>
                    </tr>
                )}
            </tbody>
        );
    };
}
